import * as React from 'react'

import { sanitizeHtmlValue } from '@/lib/element-schema-sanitizers'
import { cn } from '@/lib/utils'
import useLocale from '@/hooks/use-locale'

import { PageHeader, PageHeaderDescription, PageHeaderHeading } from '@/components/common/page-header'
import { Shell } from '@/components/common/shell'

import type { BackgroundValue, SectionElementSchema } from '@/types/element-schema.type'

/**
 * Props for SectionHeroLearningPath
 * Supports full CMS configuration via Puck editor
 */
interface SectionHeroLearningPathProps {
  /** Main heading text (supports HTML) */
  heading?: React.ReactNode
  /** Description text (supports HTML) */
  description?: React.ReactNode
  /** Additional CSS classes */
  className?: string
  // === Background Configuration ===
  /** Background type */
  bgType?: 'none' | 'color' | 'gradient' | 'image' | 'video'
  /** Solid background color */
  bgColor?: string
  /** Gradient start color */
  bgGradientFrom?: string
  /** Gradient end color */
  bgGradientTo?: string
  /** Gradient direction */
  bgGradientDirection?: 'to-r' | 'to-l' | 'to-b' | 'to-t'
  /** Gradient opacity (95 = 95%) */
  bgGradientOpacity?: '100' | '95' | '90' | '80' | '70' | '60'
  /** Background image URL */
  bgImage?: string
  /** Background video URL */
  bgVideo?: string
  /** Show overlay on image/video */
  overlay?: boolean
  // === Image Position & Size ===
  /** CSS background-position for the background image */
  bgImagePosition?: string
  /** CSS background-size for the background image */
  bgImageSize?: string
  // === Layout Configuration ===
  /** Text alignment */
  textAlignment?: 'left' | 'center' | 'right'
  /** Minimum height */
  minHeight?: 'auto' | '50vh' | '100vh' | 'custom'
  /** Container max width */
  containerWidth?: 'default' | 'narrow' | 'wide' | 'full'
  /** Full background configuration object (alternative to individual props) */
  background?: BackgroundValue
}

/**
 * Get text alignment classes
 */
function getTextAlignClasses(alignment?: string): string {
  const alignMap: Record<string, string> = {
    left: 'text-left items-start justify-start',
    center: 'text-center items-center justify-center',
    right: 'text-right items-end justify-end',
  }
  return alignMap[alignment || 'left'] || 'text-left items-start justify-start'
}

/**
 * Get gradient direction for CSS
 */
function getGradientDirection(direction?: string): string {
  const dirMap: Record<string, string> = {
    'to-r': 'to right',
    'to-l': 'to left',
    'to-b': 'to bottom',
    'to-t': 'to top',
  }
  return dirMap[direction || 'to-r'] || 'to right'
}

/**
 * Get min height classes
 */
function getMinHeightClass(height?: string): string {
  const heightMap: Record<string, string> = {
    auto: '',
    '50vh': 'min-h-[50vh]',
    '100vh': 'min-h-screen',
    custom: 'md:min-h-[calc(100vh-80px)]',
  }
  return heightMap[height || 'custom'] || 'md:min-h-[calc(100vh-80px)]'
}

/**
 * Get container width classes
 */
function getContainerWidthClass(width?: string): string {
  const widthMap: Record<string, string> = {
    default: 'max-w-[80.25rem]',
    narrow: 'max-w-4xl',
    wide: 'max-w-7xl',
    full: 'max-w-full',
  }
  return widthMap[width || 'default'] || 'max-w-[80.25rem]'
}

function SectionHeroLearningPathSection({
  heading,
  description,
  className,
  // Background props
  bgType = 'image',
  bgColor,
  bgGradientFrom = '#bf2318',
  bgGradientTo = '#a52118',
  bgGradientDirection = 'to-r',
  bgGradientOpacity = '95',
  bgImage = '/images/home/background-learning-path-page.png',
  bgVideo,
  overlay = true,
  // Image position & size
  bgImagePosition = 'center center',
  bgImageSize = 'cover',
  // Layout props
  textAlignment = 'left',
  minHeight = 'custom',
  containerWidth = 'default',
  // Full background object (alternative)
  background,
}: SectionHeroLearningPathProps) {
  const { text } = useLocale()

  // Use individual props or background object
  const actualBgType = background?.type || bgType
  const actualGradientFrom = background?.gradient?.colors?.[0] || bgGradientFrom
  const actualGradientTo = background?.gradient?.colors?.[1] || bgGradientTo
  const actualBgImage = background?.image?.src || bgImage
  const actualBgVideo = background?.video?.src || bgVideo

  // Render video background
  if (actualBgType === 'video' && actualBgVideo) {
    return (
      <div className={cn('relative z-10 overflow-hidden', className)}>
        <video
          className="absolute inset-0 w-full h-full object-cover"
          src={actualBgVideo}
          autoPlay
          loop
          muted
          playsInline
        />
        {overlay && (
          <div
            className="pointer-events-none absolute inset-0 z-0"
            style={{
              background: `linear-gradient(${getGradientDirection(bgGradientDirection)}, ${actualGradientFrom}dd, ${actualGradientTo}99)`,
            }}
          />
        )}
        <div className={cn('relative z-10 flex items-center', getMinHeightClass(minHeight))}>
          <Shell>
            <PageHeader
              className={cn(
                'space-y-4 sm:space-y-6 md:space-y-8 w-full mx-auto mt-6',
                getTextAlignClasses(textAlignment),
                getContainerWidthClass(containerWidth)
              )}
            >
              <PageHeaderHeading
                as="h2"
                size="lg"
                className="tracking-normal text-3xl sm:text-4xl md:text-5xl lg:text-6xl/tight md:leading-tight font-extrabold text-white"
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtmlValue((heading as string) || text.MAIN.INTRODUCTION.HEADING_2),
                }}
              />
              <PageHeaderDescription
                className={cn('text-base sm:text-lg md:text-xl text-white !mt-2', getTextAlignClasses(textAlignment))}
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtmlValue((description as string) || text.MAIN.INTRODUCTION.HEADING_3),
                }}
              />
            </PageHeader>
          </Shell>
        </div>
      </div>
    )
  }

  // Render image or gradient background
  const hasBgImage = actualBgType === 'image' && actualBgImage
  const hasGradient = actualBgType === 'gradient' || (hasBgImage && overlay)
  const hasSolidColor = actualBgType === 'color' && bgColor

  return (
    <div className={cn('relative z-10', className)} style={hasSolidColor ? { backgroundColor: bgColor } : undefined}>
      {/* Gradient color layer (bottom) */}
      {hasGradient && (
        <div
          className="pointer-events-none absolute inset-0 z-0"
          style={{
            background: `linear-gradient(${getGradientDirection(bgGradientDirection)}, ${actualGradientFrom}${bgGradientOpacity === '100' ? '' : bgGradientOpacity}, ${actualGradientTo}${bgGradientOpacity === '100' ? '' : '60'})`,
          }}
        />
      )}
      {/* Background image layer (on top of gradient) */}
      {hasBgImage && (
        <div
          className="absolute inset-0 z-10 bg-no-repeat"
          style={{
            backgroundImage: `url(${actualBgImage})`,
            backgroundPosition: bgImagePosition,
            backgroundSize: bgImageSize,
          }}
        />
      )}
      {/* Main content (top) */}
      <div className={cn('overflow-hidden relative z-20 flex items-center', getMinHeightClass(minHeight))}>
        <Shell>
          <PageHeader
            className={cn(
              'space-y-4 sm:space-y-6 md:space-y-8 w-full mx-auto mt-6',
              getTextAlignClasses(textAlignment),
              getContainerWidthClass(containerWidth)
            )}
          >
            <PageHeaderHeading
              as="h2"
              size="lg"
              className="tracking-normal text-3xl sm:text-4xl md:text-5xl lg:text-6xl/tight md:leading-tight font-extrabold text-white"
              dangerouslySetInnerHTML={{
                __html: sanitizeHtmlValue((heading as string) || text.MAIN.INTRODUCTION.HEADING_2),
              }}
            />
            <PageHeaderDescription
              className={cn('text-base sm:text-lg md:text-xl text-white !mt-2', getTextAlignClasses(textAlignment))}
              dangerouslySetInnerHTML={{
                __html: sanitizeHtmlValue((description as string) || text.MAIN.INTRODUCTION.HEADING_3),
              }}
            />
          </PageHeader>
        </Shell>
      </div>
    </div>
  )
}

/**
 * Element schema for SectionHeroLearningPath
 * Defines all editable elements for the learning path hero section
 */
SectionHeroLearningPathSection.elementSchema = {
  componentType: 'SectionHeroLearningPath',
  label: 'Learning Path Hero',
  description: 'Hero section for learning path page with configurable background and layout',
  category: 'hero',
  tags: ['learning-path', 'education'],
  elements: [
    // Text Content
    {
      key: 'heading',
      type: 'textarea',
      label: 'Heading',
      helpText: 'Main heading text (supports HTML)',
      default: 'Trở thành Full Stack <br />Data Scientist',
    },
    {
      key: 'description',
      type: 'textarea',
      label: 'Description',
      helpText: 'Subheading or description text (supports HTML)',
      default:
        'Hành trình từ con số 0 tới làm chủ 4 trụ cột kiến thức: <br /> <b>Modeling, Domain Knowledge, Analytics</b> và <b>Engineering</b>.',
    },
    // Layout
    {
      key: 'textAlignment',
      type: 'select',
      label: 'Text Alignment',
      options: [
        { label: 'Left', value: 'left' },
        { label: 'Center', value: 'center' },
        { label: 'Right', value: 'right' },
      ],
      default: 'left',
    },
    {
      key: 'minHeight',
      type: 'select',
      label: 'Minimum Height',
      options: [
        { label: 'Auto', value: 'auto' },
        { label: 'Half Screen', value: '50vh' },
        { label: 'Full Screen', value: '100vh' },
        { label: 'Custom (calc)', value: 'custom' },
      ],
      default: 'custom',
    },
    {
      key: 'containerWidth',
      type: 'select',
      label: 'Container Width',
      options: [
        { label: 'Default', value: 'default' },
        { label: 'Narrow', value: 'narrow' },
        { label: 'Wide', value: 'wide' },
        { label: 'Full', value: 'full' },
      ],
      default: 'default',
    },
    // Background Configuration
    {
      key: 'bgType',
      type: 'select',
      label: 'Background Type',
      options: [
        { label: 'None', value: 'none' },
        { label: 'Color', value: 'color' },
        { label: 'Gradient', value: 'gradient' },
        { label: 'Image', value: 'image' },
        { label: 'Video', value: 'video' },
      ],
      default: 'image',
    },
    {
      key: 'bgColor',
      type: 'color',
      label: 'Background Color',
      helpText: 'Solid background color (when Type is Color)',
    },
    {
      key: 'bgGradientFrom',
      type: 'color',
      label: 'Gradient Start',
      helpText: 'Starting color for gradient overlay',
      default: '#bf2318',
    },
    {
      key: 'bgGradientTo',
      type: 'color',
      label: 'Gradient End',
      helpText: 'Ending color for gradient overlay',
      default: '#a52118',
    },
    {
      key: 'bgGradientDirection',
      type: 'select',
      label: 'Gradient Direction',
      options: [
        { label: 'Left to Right', value: 'to-r' },
        { label: 'Right to Left', value: 'to-l' },
        { label: 'Top to Bottom', value: 'to-b' },
        { label: 'Bottom to Top', value: 'to-t' },
      ],
      default: 'to-r',
    },
    {
      key: 'bgGradientOpacity',
      type: 'select',
      label: 'Gradient Opacity',
      options: [
        { label: '100%', value: '100' },
        { label: '95%', value: '95' },
        { label: '90%', value: '90' },
        { label: '80%', value: '80' },
        { label: '70%', value: '70' },
        { label: '60%', value: '60' },
      ],
      default: '95',
    },
    {
      key: 'bgImage',
      type: 'image',
      label: 'Background Image',
      helpText: 'URL for background image (when Type is Image)',
      default: '/images/home/background-learning-path-page.png',
    },
    {
      key: 'bgVideo',
      type: 'video',
      label: 'Background Video',
      helpText: 'URL for background video (when Type is Video)',
    },
    {
      key: 'overlay',
      type: 'boolean',
      label: 'Show Overlay',
      helpText: 'Add gradient overlay on top of image/video',
      default: true,
    },
    // Image Position & Size
    {
      key: 'bgImagePosition',
      type: 'select',
      label: 'Image Position',
      helpText: 'CSS background-position for the background image',
      options: [
        { label: 'Center', value: 'center center' },
        { label: 'Top', value: 'center top' },
        { label: 'Bottom', value: 'center bottom' },
        { label: 'Left', value: 'left center' },
        { label: 'Right', value: 'right center' },
        { label: 'Top Left', value: 'left top' },
        { label: 'Top Right', value: 'right top' },
        { label: 'Bottom Left', value: 'left bottom' },
        { label: 'Bottom Right', value: 'right bottom' },
      ],
      default: 'center center',
    },
    {
      key: 'bgImageSize',
      type: 'select',
      label: 'Image Size',
      helpText: 'CSS background-size for the background image',
      options: [
        { label: 'Cover', value: 'cover' },
        { label: 'Contain', value: 'contain' },
        { label: 'Auto', value: 'auto' },
        { label: '100%', value: '100%' },
        { label: '150%', value: '150%' },
        { label: '200%', value: '200%' },
      ],
      default: 'cover',
    },
    // Styling
    {
      key: 'className',
      type: 'text',
      label: 'CSS Class',
      helpText: 'Additional CSS classes for custom styling',
    },
  ],
} as SectionElementSchema

export default SectionHeroLearningPathSection
